/**
 * ExplorationPhase — Step 0 (pre-Psychology-Map) research phase for the AFFILIATE
 * creative flow. Drives the Chunk B backend engine
 * (/creative/sessions/{id}/exploration/*) and walks its state machine:
 *
 *   TARGETS_PROPOSED -> TARGETS_CONFIRMED -> FINDINGS_READY
 *        ^ (message may re-propose, looping back)        |
 *        └──────────────────────────────────────────────┘
 *   lock (always available) -> BRIEF_IN_REVIEW -> approve -> LOCKED -> onProceed()
 *                                              -> revise  -> BRIEF_IN_REVIEW
 *
 * Skippable at any time (onSkip) → lands in today's cold-start Psychology Map.
 * Matches the existing creative-engine visual language (slate cards, emerald
 * accents, "...ing" busy labels, data-testid hooks).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_OPTIONS = ['TRENDS', 'SOCIAL', 'COMPETITOR_ADS', 'SEARCH', 'PAGE_SCRAPE'];
const SOURCE_OPTIONS = [
  { name: 'google_trends', label: 'Google Trends' },
  { name: 'reddit', label: 'Reddit' },
  { name: 'competitor_ads', label: 'Competitor Ads' },
  { name: 'google_search', label: 'Google Search' },
];

const ExplorationPhase = ({ sessionId, offer, onProceed, onSkip }) => {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('');
  const [error, setError] = useState(null);

  // Local editable target/source state for the TARGETS_PROPOSED stage.
  const [editTargets, setEditTargets] = useState([]);
  const [editSources, setEditSources] = useState(SOURCE_OPTIONS.map(s => s.name));
  const [messageText, setMessageText] = useState('');
  const [feedback, setFeedback] = useState('');

  const call = useCallback(async (label, fn) => {
    setBusy(true); setBusyLabel(label); setError(null);
    try {
      const res = await fn();
      if (res && res.data) setDoc(res.data);
      return res?.data;
    } catch (e) {
      console.error('Exploration action failed:', e);
      setError(e.response?.data?.detail || e.message || 'Something went wrong');
      return null;
    } finally {
      setBusy(false); setBusyLabel('');
    }
  }, []);

  // On mount: start (idempotent — returns the in-progress doc if one exists).
  useEffect(() => {
    if (!sessionId) return;
    call('Proposing research targets', () =>
      axios.post(`${API}/creative/sessions/${sessionId}/exploration/start`));
  }, [sessionId, call]);

  // Auto-proceed once the brief is approved/locked.
  useEffect(() => {
    if (doc?.state === 'LOCKED') {
      const t = setTimeout(() => onProceed && onProceed(), 600);
      return () => clearTimeout(t);
    }
  }, [doc?.state, onProceed]);

  // Sync the editable list whenever the engine (re)proposes targets.
  useEffect(() => {
    if (doc?.state === 'TARGETS_PROPOSED') {
      setEditTargets((doc.proposed_targets || []).map(t => ({
        label: t.label || '', source_kind: (t.source_kind || 'SEARCH').toUpperCase(),
      })));
      if (Array.isArray(doc.selected_sources) && doc.selected_sources.length) {
        setEditSources(doc.selected_sources);
      }
    }
  }, [doc?.state, doc?.proposed_targets, doc?.selected_sources]);

  const state = doc?.state;
  const latestRound = doc?.fetch_rounds?.length ? doc.fetch_rounds[doc.fetch_rounds.length - 1] : null;
  const budget = doc?.budget || {};
  const fetchExhausted = (budget.fetch_rounds_used || 0) >= (budget.max_fetch_rounds || 3);

  // ── actions ──
  const confirmTargets = () => call('Confirming targets', () =>
    axios.post(`${API}/creative/sessions/${sessionId}/exploration/targets/confirm`, {
      confirmed_targets: editTargets.filter(t => t.label.trim()),
      sources: editSources,
    }));
  const runFetch = () => call('Researching (this can take up to a minute)', () =>
    axios.post(`${API}/creative/sessions/${sessionId}/exploration/fetch`));
  const sendMessage = async () => {
    if (!messageText.trim()) return;
    const text = messageText.trim();
    setMessageText('');
    await call('Thinking', () =>
      axios.post(`${API}/creative/sessions/${sessionId}/exploration/message`, { text }));
  };
  const lockAndProceed = () => call('Synthesizing brief', () =>
    axios.post(`${API}/creative/sessions/${sessionId}/exploration/lock`));
  const approveBrief = () => call('Locking brief', () =>
    axios.post(`${API}/creative/sessions/${sessionId}/exploration/brief/review`, { decision: 'approve' }));
  const reviseBrief = () => {
    const fb = feedback.trim();
    setFeedback('');
    return call('Revising brief', () =>
      axios.post(`${API}/creative/sessions/${sessionId}/exploration/brief/review`,
        { decision: 'revise', feedback: fb }));
  };

  // ── target editing helpers ──
  const updateTarget = (i, patch) =>
    setEditTargets(ts => ts.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const removeTarget = (i) => setEditTargets(ts => ts.filter((_, idx) => idx !== i));
  const addTarget = () => setEditTargets(ts => [...ts, { label: '', source_kind: 'SEARCH' }]);
  const toggleSource = (name) =>
    setEditSources(s => s.includes(name) ? s.filter(x => x !== name) : [...s, name]);

  const showLock = ['TARGETS_PROPOSED', 'TARGETS_CONFIRMED', 'FINDINGS_READY'].includes(state);

  return (
    <div className="space-y-6" data-testid="exploration-phase">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-0.5">
              Step 0 · Exploration
            </span>
          </div>
          <h3 className="text-lg font-semibold text-white mt-2">Research before the psychology map</h3>
          <p className="text-slate-400 text-sm mt-1">
            Optional. Confirm what to research, review what comes back, then lock a brief that
            grounds the psychology map. Skip to go straight to a cold-start map.
          </p>
        </div>
        <button
          onClick={onSkip}
          disabled={busy}
          data-testid="exploration-skip-btn"
          className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 disabled:opacity-50 shrink-0"
        >
          Skip → Psychology Map
        </button>
      </div>

      {error && (
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg px-4 py-3 text-amber-300 text-sm" data-testid="exploration-error">
          {error}
        </div>
      )}

      {busy && (
        <div className="flex items-center gap-3 text-slate-400 text-sm" data-testid="exploration-busy">
          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-emerald-500" />
          {busyLabel}…
        </div>
      )}

      {/* ── TARGETS_PROPOSED: review/edit proposed targets + sources ── */}
      {state === 'TARGETS_PROPOSED' && (
        <div className="bg-slate-900/30 border border-slate-800 rounded-xl p-6 space-y-5" data-testid="exploration-propose">
          <div>
            <h4 className="text-white font-semibold mb-1">Proposed research targets</h4>
            <p className="text-slate-400 text-sm mb-3">Edit, remove, or add targets. Each routes to its source by type.</p>
            <div className="space-y-2">
              {editTargets.map((t, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`exploration-target-${i}`}>
                  <select
                    value={t.source_kind}
                    onChange={(e) => updateTarget(i, { source_kind: e.target.value })}
                    className="bg-slate-800 border border-slate-700 text-slate-200 rounded-lg px-2 py-2 text-sm"
                  >
                    {KIND_OPTIONS.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <input
                    value={t.label}
                    onChange={(e) => updateTarget(i, { label: e.target.value })}
                    placeholder="research term…"
                    className="flex-1 bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm"
                  />
                  <button onClick={() => removeTarget(i)} className="text-slate-500 hover:text-red-400 px-2" title="Remove">✕</button>
                </div>
              ))}
            </div>
            <button onClick={addTarget} className="mt-2 text-emerald-400 hover:text-emerald-300 text-sm">+ Add target</button>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-2">Sources to query</h4>
            <div className="flex flex-wrap gap-2">
              {SOURCE_OPTIONS.map(s => (
                <button
                  key={s.name}
                  onClick={() => toggleSource(s.name)}
                  data-testid={`exploration-source-toggle-${s.name}`}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${
                    editSources.includes(s.name)
                      ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                      : 'bg-slate-800 border-slate-700 text-slate-400'
                  }`}
                >
                  {editSources.includes(s.name) ? '✓ ' : ''}{s.label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={confirmTargets}
            disabled={busy || !editTargets.some(t => t.label.trim()) || !editSources.length}
            data-testid="exploration-confirm-btn"
            className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
          >
            Confirm targets
          </button>
        </div>
      )}

      {/* ── TARGETS_CONFIRMED: ready to fetch ── */}
      {state === 'TARGETS_CONFIRMED' && (
        <div className="bg-slate-900/30 border border-slate-800 rounded-xl p-6 space-y-4" data-testid="exploration-confirmed">
          <h4 className="text-white font-semibold">Targets confirmed</h4>
          <div className="flex flex-wrap gap-2">
            {(doc.confirmed_targets || []).map((t, i) => (
              <span key={i} className="text-sm text-slate-300 bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5">
                <span className="text-emerald-400 text-xs mr-1">{t.source_kind}</span>{t.label}
              </span>
            ))}
          </div>
          <p className="text-slate-500 text-sm">Sources: {(doc.selected_sources || []).join(', ')}</p>
          <button
            onClick={runFetch}
            disabled={busy}
            data-testid="exploration-fetch-btn"
            className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50"
          >
            {busy ? 'Researching…' : 'Run research'}
          </button>
        </div>
      )}

      {/* ── FINDINGS_READY: per-source results + multi-turn steering ── */}
      {state === 'FINDINGS_READY' && (
        <div className="space-y-5" data-testid="exploration-findings">
          <div className="bg-slate-900/30 border border-slate-800 rounded-xl p-6">
            <h4 className="text-white font-semibold mb-3">Research results</h4>
            <div className="space-y-3">
              {latestRound && latestRound.results.map((res, idx) => {
                const q = latestRound.queries?.[idx] || {};
                const items = res.items || [];
                const failed = res.ok === false;
                const empty = res.ok && items.length === 0;
                return (
                  <div key={idx} className="border border-slate-800 rounded-lg p-4" data-testid={`exploration-source-result-${res.source}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-white text-sm font-medium">
                        {res.source}
                        <span className="text-slate-500 text-xs ml-2">query: “{q.topic}”</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        failed ? 'bg-slate-700 text-slate-400'
                          : empty ? 'bg-slate-700 text-slate-400'
                          : 'bg-emerald-500/15 text-emerald-300'
                      }`}>
                        {failed ? 'no results' : empty ? 'no results' : `${items.length} found`}
                      </span>
                    </div>
                    {failed && <p className="text-slate-500 text-xs mt-1">Source returned nothing this run.</p>}
                    {items.slice(0, 2).map((it, j) => (
                      <div key={j} className="mt-2 text-sm">
                        <div className="text-slate-200">{(it.title || '').slice(0, 100)}</div>
                        {it.body && <div className="text-slate-500 text-xs mt-0.5">{(it.body || '').slice(0, 140)}</div>}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            {!fetchExhausted && (
              <button onClick={runFetch} disabled={busy} data-testid="exploration-fetch-more-btn"
                className="mt-4 text-emerald-400 hover:text-emerald-300 text-sm disabled:opacity-50">
                ↻ Run another research round
              </button>
            )}
          </div>

          {/* Conversation / steering */}
          <div className="bg-slate-900/30 border border-slate-800 rounded-xl p-6">
            <h4 className="text-white font-semibold mb-3">Steer the research</h4>
            <div className="space-y-2 max-h-64 overflow-y-auto mb-3" data-testid="exploration-conversation">
              {(doc.conversation || []).map((m, i) => (
                <div key={i} className={`text-sm rounded-lg px-3 py-2 ${
                  m.role === 'operator' ? 'bg-emerald-500/10 text-emerald-100 ml-8' : 'bg-slate-800 text-slate-300 mr-8'
                }`}>
                  {m.content}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !busy) sendMessage(); }}
                placeholder='e.g. "go deeper on competitor angles"'
                data-testid="exploration-message-input"
                className="flex-1 bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm"
              />
              <button onClick={sendMessage} disabled={busy || !messageText.trim()}
                data-testid="exploration-message-send-btn"
                className="px-4 py-2 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 disabled:opacity-50">
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── BRIEF_IN_REVIEW: approve or revise ── */}
      {state === 'BRIEF_IN_REVIEW' && (
        <div className="bg-slate-900/30 border border-slate-800 rounded-xl p-6 space-y-4" data-testid="exploration-brief-review">
          <div className="flex items-center justify-between">
            <h4 className="text-white font-semibold">Research brief</h4>
            <span className="text-slate-500 text-xs">v{doc.brief?.version}</span>
          </div>
          <div className="bg-slate-950/50 border border-slate-800 rounded-lg p-4 text-slate-200 text-sm whitespace-pre-wrap max-h-96 overflow-y-auto" data-testid="exploration-brief-text">
            {doc.brief?.text}
          </div>
          <div className="flex flex-col gap-3">
            <button onClick={approveBrief} disabled={busy}
              data-testid="exploration-approve-btn"
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 self-start">
              {busy ? 'Locking…' : 'Approve & continue to Psychology Map'}
            </button>
            <div>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Or give feedback to revise the brief…"
                data-testid="exploration-feedback-input"
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-2 text-sm"
                rows={2}
              />
              <button onClick={reviseBrief} disabled={busy || !feedback.trim()}
                data-testid="exploration-revise-btn"
                className="mt-2 px-4 py-2 bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 disabled:opacity-50">
                Request revision
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── LOCKED: proceeding ── */}
      {state === 'LOCKED' && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 text-emerald-300" data-testid="exploration-locked">
          Research locked — continuing to the Psychology Map…
        </div>
      )}

      {/* Always-available lock during research stages */}
      {showLock && (
        <div className="flex justify-end">
          <button onClick={lockAndProceed} disabled={busy}
            data-testid="exploration-lock-btn"
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {busy ? 'Working…' : 'Lock & proceed → synthesize brief'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ExplorationPhase;
